import {forwardRef, useState, useEffect} from "react";
import {PlatformCanvasDrawer} from "./PlatformCanvasDrawer";

export const PlatformChangeRequestForm = forwardRef(({onUpdate, active, isViewOnly = false, initialData}, ref) => {

    const [formData, setFormData] = useState({
        platformDescription: '',
        requestedDate: '',
        specialInstructions: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
    });

    // Populate form with initial data when provided
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData(prevState => ({
                ...prevState,
                ...initialData
            }));
        }
    }, [initialData]);

    const onChange = (event) => {
        const { name, value } = event.target;
        const newData = { ...formData, [name]: value };
        setFormData(newData);
        onUpdate(newData); // Pass updated data to parent
    };

    if (!active) return null; // Only render if active

    return (
        <div className="rounded-lg">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                    <p className="text-sm text-gray-500 mb-4">
                        Use this form to request changes to the platform setup. Your diagram will help our team understand your needs more clearly.
                    </p>
                </div>
                
                <div className="sm:col-span-6">
                    <label htmlFor="platformDescription" className="block text-sm font-medium text-gray-700">Platform Setup Description</label>
                    <div className="mt-1">
                        <textarea
                            name="platformDescription"
                            id="platformDescription"
                            rows="4"
                            value={formData.platformDescription}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Describe what the platform needs to look like, including dimensions, heights, and any special requirements."
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="requestedDate" className="block text-sm font-medium text-gray-700">Date Needed By</label>
                    <div className="mt-1">
                        <input
                            type="date"
                            name="requestedDate"
                            id="requestedDate"
                            value={formData.requestedDate}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700">Contact Person</label>
                    <div className="mt-1">
                        <input
                            type="text"
                            name="contactPerson"
                            id="contactPerson"
                            value={formData.contactPerson}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Person to contact for questions"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">Contact Email</label>
                    <div className="mt-1">
                        <input
                            type="email"
                            name="contactEmail"
                            id="contactEmail"
                            value={formData.contactEmail}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Email address"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <div className="mt-1">
                        <input
                            type="tel"
                            name="contactPhone"
                            id="contactPhone"
                            value={formData.contactPhone}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Phone number"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-6">
                    <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700">Special Instructions</label>
                    <div className="mt-1">
                        <textarea
                            name="specialInstructions"
                            id="specialInstructions"
                            rows="3"
                            value={formData.specialInstructions}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Any additional instructions or requirements"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-6 border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Platform Diagram</label>
                    <p className="text-sm text-gray-500 mb-4">Use the canvas below to draw a diagram of your desired platform setup.</p>
                    <div className="bg-gray-50 p-4 rounded-md border border-gray-300">
                        <PlatformCanvasDrawer ref={ref} />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">The diagram will be automatically saved when you submit the form.</p>
                </div>
            </div>
        </div>
    );
});